import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onDrag", "onDragStart", "onDragEnd", "onMouseDown", "children", "flexGrow"];
import { syntheticEvent } from '../../utils/SyntheticEvent/SyntheticEvent';
import { isLeftClick } from '../../utils/isLeftClick/isLeftClick';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { callIfPossible } from '../../utils/functions/functions';
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledAbstractSlider = styled.div.withConfig({
  displayName: "AbstractSlider__StyledAbstractSlider",
  componentId: "sc-1jyjpam-0"
})(({
  $flexGrow,
  $cssStyles
}) => {
  return Object.assign({
    flexGrow: $flexGrow
  }, $cssStyles);
});
export const AbstractSlider = _ref => {
  let {
      onDrag,
      onDragStart,
      onDragEnd,
      onMouseDown,
      children,
      flexGrow
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const _el = useRef(null);

  // These callback props & effects are dependencies of useCallback in this component, so we use refs to ensure that updates to these props don't cause the callback to be redefined
  const onDragRef = useRef(onDrag);
  const onDragEndRef = useRef(onDragEnd);
  useLayoutEffect(() => {
    onDragRef.current = onDrag;
  }, [onDrag]);
  useLayoutEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);
  const updatePosition = useCallback(clientX => {
    if (_el.current == null) return;
    const {
      left,
      right
    } = _el.current.getBoundingClientRect();
    if (left === right) return;
    let DragPercentX = (clientX - left) / (right - left);
    if (DragPercentX < 0) DragPercentX = 0;
    if (DragPercentX > 1) DragPercentX = 1;
    callIfPossible(onDragRef.current, syntheticEvent({
      percentX: DragPercentX
    }));
  }, []);
  const handleWindowMouseMove = useCallback(evt => {
    updatePosition(evt.clientX);
  }, [updatePosition]);
  const handleWindowMouseUp = useCallback(() => {
    callIfPossible(onDragEndRef.current);
    window.removeEventListener('mousemove', handleWindowMouseMove);
    window.removeEventListener('mouseup', handleWindowMouseUp);
  }, [handleWindowMouseMove]);
  const bindWindowListeners = () => {
    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
  };
  const handleMouseDown = evt => {
    callIfPossible(onMouseDown, evt);
    if (evt.defaultPrevented) return;
    if (isLeftClick(evt)) {
      updatePosition(evt.clientX);
      callIfPossible(onDragStart);
      bindWindowListeners();
    }
  };
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [handleWindowMouseMove, handleWindowMouseUp]);
  return /*#__PURE__*/_jsx(StyledAbstractSlider, Object.assign({}, rest, {
    $flexGrow: flexGrow,
    onMouseDown: handleMouseDown,
    ref: _el,
    $cssStyles: cssStyles
  }, domProps, {
    children: children
  }));
};